<template>
  <div
    :class="['ExpiryMessage', isPromoExpired || isSignupExpired ? expiredClasses : defaultClasses]"
    v-if="showMessage"
    v-html="message"
  ></div>
</template>

<script>
  import { formatLocalFromUTC } from '@/Utils/date';
  import { DateTime } from 'luxon';
  import humanize from 'humanize-duration';

  export default {
    name: 'ExpiryMessage',

    props: {
      promo: {
        type: Object,
      },
      expiredClasses: {
        type: String,
        default: '',
      },
      defaultClasses: {
        type: String,
        default: '',
      },
      hideTitle: {
        type: Boolean,
        default: false,
      },
    },

    computed: {
      showMessage() {
        return (
          this.promo.ends_at ||
          (this.promo.readers_end_at && !this.promo.permissions?.canListen) ||
          (this.promo.sale_price_ends_at && !this.isSaleExpired)
        );
      },

      promoExpireDiff() {
        let now = new Date();
        let expireDateTime = DateTime.fromISO(this.promo.ends_at_iso);
        let diff = expireDateTime.toMillis() - now.getTime();
        return isNaN(diff) ? null : diff;
      },

      isPromoExpired() {
        return this.promoExpireDiff < 0;
      },

      signupExpireDiff() {
        let now = new Date();
        let expireDateTime = DateTime.fromISO(this.promo.readers_end_at);
        let diff = expireDateTime.toMillis() - now.getTime();
        return isNaN(diff) ? null : diff;
      },

      isSignupExpired() {
        return this.signupExpireDiff < 0;
      },

      saleExpireDiff() {
        let now = new Date();
        let expireDateTime = DateTime.fromISO(this.promo.sale_price_ends_at_iso);
        let diff = expireDateTime.toMillis() - now.getTime();
        return isNaN(diff) ? null : diff;
      },

      isSaleExpired() {
        return this.saleExpireDiff < 0;
      },

      message() {
        let promoFormattedDiff =
          this.promoExpireDiff !== null
            ? humanize(this.promoExpireDiff, { largest: 1, round: true })
            : null;
        let promoFormattedExpireDate = formatLocalFromUTC(this.promo.ends_at_iso);
        let signupFormattedDiff =
          this.signupExpireDiff !== null
            ? humanize(this.signupExpireDiff, { largest: 1, round: true })
            : null;
        let signupFormattedExpireDate = formatLocalFromUTC(this.promo.readers_end_at_iso);
        let saleFormattedExpireDate = formatLocalFromUTC(this.promo.sale_price_ends_at_iso);
        let saleFormattedDiff =
          this.saleExpireDiff !== null
            ? humanize(this.saleExpireDiff, { largest: 1, round: true })
            : null;

        let title = this.promo.can_purchase
          ? `<strong>FREE access</strong>${this.hideTitle ? '' : ' for '}`
          : '';

        if (!this.hideTitle) {
          title += `"${this.promo.name}"`;
        }

        if (this.isPromoExpired) {
          return `${
            title ? `${title} expired` : 'Expired'
          } ${promoFormattedDiff} ago (${promoFormattedExpireDate})`;
        }

        if (this.isSignupExpired) {
          return `New signups${
            this.hideTitle ? '' : ' for'
          } ${title} expired ${signupFormattedDiff} ago (${signupFormattedExpireDate})`;
        }

        let i = -1;
        let ndx = -1;
        const diffs = [this.promoExpireDiff, this.signupExpireDiff, this.saleExpireDiff];
        let formattedDiff = diffs.reduce((num, diff) => {
          i++;
          if (diff < num && diff !== null) {
            ndx = i;
            return diff;
          }
          return num;
        }, 9999 * 9999 * 9999);

        let expiresIn = 0;
        switch (ndx) {
          case 0:
            expiresIn = `${promoFormattedDiff} (${promoFormattedExpireDate})`;
            return `The link for this promotional offer ${title ? `of ${title}` : ''} expires in ${expiresIn}`;

          case 1:
            expiresIn = `${signupFormattedDiff} (${signupFormattedExpireDate})`;
            return `The link for this promotional offer ${title ? `of ${title}` : ''} expires in ${expiresIn}`;

          case 2:
            if (!this.isSaleExpired) {
              expiresIn = `${saleFormattedDiff} (${saleFormattedExpireDate})`;
              return `${title ? `Sale for ${title} ends` : 'Sale ends'} in ${expiresIn}`;
            }
        }
      },
    },
  };
</script>

<!-- <style lang="scss" src="./ExpiryMessage.scss" scoped></style> -->
